import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppState } from "@/state/AppStateProvider";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { ScheduleAssetDialog } from "@/components/content/ScheduleAssetDialog";
import { PlatformPreviewPanel } from "@/components/content/PlatformPreviewPanel";
import { IgCardControl } from "@/components/content/IgCardControl";
import { ASSET_TYPE_LABELS } from "@/lib/constants";
import { getAssetReviewWorkflow } from "@/lib/reviewWorkflow";
import { getPlatformPreviewMeta } from "@/lib/postReviewWorkspace";
import { getAssetCoverPrompt } from "@/lib/imagePromptAssets";
import { imageAspectForPlatform } from "@/lib/mediaAspect";
import { deriveLibrarySubject } from "@/lib/libraryWorkspace";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  FolderKanban,
  Wand2,
  Hash,
  ExternalLink,
  Copy,
  Save,
  CheckCheck,
  CalendarPlus,
  RotateCw,
  CheckCircle2,
  History,
  Undo2,
  ListChecks,
  Send,
  Loader2,
  Image as ImageIcon,
  X as XIcon,
  Trash2,
  Pencil,
  Sparkles,
  Upload,
} from "lucide-react";
import { formatDateTime, timeAgo } from "@/lib/utils";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function AssetDetail() {
  const { id } = useParams();
  const { state, updateAsset, approveAsset, rejectAsset, publishAsset, generateAssetCover, uploadAssetCover, reviseAsset, revertAsset } = useAppState();
  const [publishing, setPublishing] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverProvider = "free"; // Cloudflare Workers AI (only image provider)
  const asset = useMemo(() => state.assets.find((a) => a.id === id), [state.assets, id]);
  const assetId = asset?.id;
  const assetHook = asset?.hook || "";
  const assetBody = asset?.body || "";
  const assetCta = asset?.cta || "";
  const assetHashtagText = Array.isArray(asset?.hashtags) ? asset.hashtags.join(" ") : "";
  const assetUpdatedAt = asset?.updated_at;
  const sourceRun = useMemo(
    () => state.runs.find((run) => (run.asset_ids || []).includes(id)),
    [state.runs, id],
  );
  const sourcePrompt = sourceRun?.prompt || "";
  const displayTitle = deriveLibrarySubject(sourcePrompt, asset?.title || "") || asset?.title || "";
  const project = useMemo(() => (asset?.project_id ? state.projects.find((p) => p.id === asset.project_id) : null), [state.projects, asset]);
  const workflow = useMemo(() => getAssetReviewWorkflow(asset || {}), [asset]);
  const [coverPrompt, setCoverPrompt] = useState(() => getAssetCoverPrompt(asset));

  const [draft, setDraft] = useState(() => ({
    hook: asset?.hook || "",
    body: asset?.body || "",
    cta: asset?.cta || "",
    hashtags: (asset?.hashtags || []).join(" "),
  }));
  const [lastSaved, setLastSaved] = useState(asset?.updated_at);
  // Post is read-only ("view") until you choose Edit (manual) or Revise (agent).
  const [mode, setMode] = useState("view"); // "view" | "edit" | "revise"
  const [busy, setBusy] = useState(null); // null | "revise" | "regenerate"
  const [instruction, setInstruction] = useState("");

  useEffect(() => {
    setCoverPrompt(getAssetCoverPrompt(asset));
  }, [asset]);

  useEffect(() => {
    if (!assetId) return;
    setDraft({
      hook: assetHook,
      body: assetBody,
      cta: assetCta,
      hashtags: assetHashtagText,
    });
    setLastSaved(assetUpdatedAt);
    setMode("view");
    setInstruction("");
  }, [assetBody, assetCta, assetHashtagText, assetHook, assetId, assetUpdatedAt]);

  // Build a "live" asset that merges draft values so the preview reflects unsaved edits.
  const livePreviewAsset = useMemo(() => {
    if (!asset) return null;
    return {
      ...asset,
      hook: draft.hook,
      body: draft.body,
      cta: draft.cta,
      hashtags: draft.hashtags.split(/\s+/).filter((t) => t.startsWith("#")),
      updated_at: lastSaved || asset.updated_at,
    };
  }, [asset, draft, lastSaved]);
  const previewMeta = useMemo(() => getPlatformPreviewMeta(livePreviewAsset || asset || {}), [asset, livePreviewAsset]);

  if (!asset) {
    return (
      <div className="space-y-4" data-testid="asset-not-found">
        <Button asChild variant="ghost" size="sm" className="gap-1.5" data-testid="asset-back">
          <Link to="/library"><ArrowLeft className="h-4 w-4" /> Back to library</Link>
        </Button>
        <div className="rounded-2xl border p-10 text-center" style={{ borderColor: "var(--border-subtle)" }}>
          <p className="text-sm text-1">Asset not found.</p>
          <p className="mt-1 text-xs text-muted-foreground">It may have been archived or removed.</p>
        </div>
      </div>
    );
  }

  const charCount = previewMeta.textLength;
  const overLimit = previewMeta.overLimit;
  const hookCount = (draft.hook || "").length;
  const bodyCount = (draft.body || "").length;
  const ctaCount = (draft.cta || "").length;
  const revisionHistory = Array.isArray(asset.revision_history) ? asset.revision_history : [];

  const onSave = () => {
    updateAsset(asset.id, {
      hook: draft.hook,
      body: draft.body,
      cta: draft.cta,
      hashtags: draft.hashtags.split(/\s+/).filter((t) => t.startsWith("#")),
      char_count: previewMeta.textLength,
    });
    setLastSaved(new Date().toISOString());
    toast.success("Saved");
  };

  const onCopy = () => {
    const text = [draft.hook, draft.body, draft.cta && draft.cta !== "—" ? draft.cta : null, draft.hashtags]
      .filter(Boolean)
      .join("\n\n");
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const onApproveAsset = () => {
    approveAsset(asset.id);
    toast.success("Approved - ready to schedule");
  };

  const onGenerateCover = async () => {
    const prompt = (coverPrompt || "").trim();
    if (!prompt) return toast.error("Add an image prompt first");
    setCoverBusy(true);
    const pending = toast.loading("Generating cover image…");
    try {
      const result = await generateAssetCover(asset.id, prompt, coverProvider, imageAspectForPlatform(asset.platform));
      if (result?.ok) toast.success("Cover image generated", { id: pending });
      else toast.error(`Image generation failed: ${result?.message || "unknown error"}`, { id: pending });
    } finally {
      setCoverBusy(false);
    }
  };

  const onUploadCover = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file");
    if (file.size > 12 * 1024 * 1024) return toast.error("That image is too large (max 12 MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      setCoverBusy(true);
      const pending = toast.loading("Uploading cover…");
      try {
        const result = await uploadAssetCover(asset.id, reader.result);
        if (result?.ok) toast.success("Cover uploaded", { id: pending });
        else toast.error(`Upload failed: ${result?.message || "unknown error"}`, { id: pending });
      } finally {
        setCoverBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const onPublishAsset = async () => {
    setPublishing(true);
    const pending = toast.loading(`Publishing to ${asset.platform}…`);
    try {
      const result = await publishAsset(asset.id);
      if (result?.ok) {
        toast.success(`Published to ${asset.platform}`, { id: pending });
      } else {
        // Outage / not-connected fallback: copy the post for manual publishing.
        if (result?.content) navigator.clipboard?.writeText(result.content);
        toast.error(`${result?.message || "Publish failed."} Copied the post so you can publish manually.`, { id: pending });
      }
    } finally {
      setPublishing(false);
    }
  };

  const cancelEdit = () => {
    setDraft({ hook: asset.hook || "", body: asset.body || "", cta: asset.cta || "", hashtags: (asset.hashtags || []).join(" ") });
    setMode("view");
  };

  // Pull the agent's revised asset back into the editable draft.
  const syncDraftFrom = (a) => {
    if (!a) return;
    setDraft((d) => ({
      ...d,
      hook: a.hook ?? d.hook,
      body: a.body ?? d.body,
      cta: a.cta ?? d.cta,
      hashtags: Array.isArray(a.hashtags) ? a.hashtags.join(" ") : d.hashtags,
    }));
    setLastSaved(new Date().toISOString());
  };

  const runRevise = async () => {
    setBusy("revise");
    try {
      const res = await reviseAsset(asset.id, { instruction: instruction.trim() || "Make this sharper", mode: "revise" });
      if (res?.ok) { syncDraftFrom(res.asset); setInstruction(""); setMode("view"); toast.success("Revision ready for review"); }
      else toast.error(`Revision failed: ${res?.message || "try again"}`);
    } finally { setBusy(null); }
  };

  const runRegenerate = async () => {
    setBusy("regenerate");
    try {
      const res = await reviseAsset(asset.id, { mode: "regenerate" });
      if (res?.ok) { syncDraftFrom(res.asset); toast.success("Fresh take ready for review"); }
      else toast.error(`Regenerate failed: ${res?.message || "try again"}`);
    } finally { setBusy(null); }
  };

  const handoffTone = workflow.canSchedule
    ? asset.status === "scheduled" ? "scheduled" : "approved"
    : workflow.isPublished ? "published" : "review";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="asset-detail-page">
      {/* Compact top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-1" data-testid="asset-back">
            <Link to="/library"><ArrowLeft className="h-4 w-4" /> Library</Link>
          </Button>
          <span className="text-muted-foreground">/</span>
          <PlatformBadge platform={asset.platform} />
          <p className="truncate text-[13.5px] text-1">{displayTitle}</p>
          <StatusBadge status={asset.status} variant="solid" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={onCopy} data-testid="asset-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
          {workflow.canSchedule ? (
            <ScheduleAssetDialog
              asset={asset}
              trigger={(
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="asset-schedule">
                  <CalendarPlus className="h-3.5 w-3.5" /> {workflow.scheduleLabel}
                </Button>
              )}
            />
          ) : workflow.isPublished && asset.public_url ? (
            <Button asChild variant="outline" size="sm" className="gap-1.5" data-testid="asset-published">
              <a href={asset.public_url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /> Published version</a>
            </Button>
          ) : workflow.isPublished ? (
            <Button variant="outline" size="sm" disabled className="gap-1.5 opacity-65" data-testid="asset-published-record">
              <ExternalLink className="h-3.5 w-3.5" /> Published record
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled className="gap-1.5 opacity-65" data-testid="asset-schedule-disabled">
              <CalendarPlus className="h-3.5 w-3.5" /> {workflow.scheduleLabel}
            </Button>
          )}
          {asset.status === "scheduled" && (
            <Button asChild variant="outline" size="sm" className="gap-1.5" data-testid="asset-view-calendar">
              <Link to="/calendar"><ExternalLink className="h-3.5 w-3.5" /> Calendar</Link>
            </Button>
          )}
        </div>
      </div>

      {/* Meta strip */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="asset-meta-strip">
        <MetaCard icon={Wand2} label="Skill" value={asset.skill_used} />
        <MetaCard icon={Hash} label="Type" value={ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type} mono />
        <MetaCard icon={CalendarIcon} label="Scheduled" value={asset.schedule_at ? formatDateTime(asset.schedule_at) : "Not scheduled"} />
        <MetaCard icon={FolderKanban} label="Project" value={
          project ? <Link to={`/projects/${project.id}`} className="text-[color:var(--accent-cyan)] hover:underline">{project.title}</Link> : "Unassigned"
        } />
      </section>

      <ReviewHandoff
        asset={asset}
        workflow={workflow}
        tone={handoffTone}
        onApprove={onApproveAsset}
        onCopy={onCopy}
        onPublish={onPublishAsset}
        publishing={publishing}
      />

      <AssetWorkflowStrip status={workflow.status} />

      {sourcePrompt && (
        <section className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="asset-source-prompt">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Original prompt</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-1">{sourcePrompt}</p>
        </section>
      )}

      {/* Main: editor (left) + live final-form preview (right, sticky) */}
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        {/* Editor column */}
        <div className="space-y-4 xl:col-span-7">
          {/* Post — read-only until you click Edit. Revise/Regenerate ask the agent. */}
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="asset-post-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Post</p>
              {mode === "view" && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setMode("edit")} data-testid="asset-edit-toggle">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setInstruction(""); setMode("revise"); }} data-testid="asset-revise-toggle" style={{ borderColor: "rgba(217,119,6,0.24)", color: "#B45309" }}>
                    <Wand2 className="h-3.5 w-3.5" /> Revise
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={runRegenerate} disabled={Boolean(busy)} data-testid="asset-regenerate" style={{ borderColor: "rgba(143, 224, 74,0.24)", color: "#0B7C42" }}>
                    {busy === "regenerate" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Regenerate
                  </Button>
                </div>
              )}
              {mode === "edit" && (
                <div className="flex items-center gap-2">
                  <span className={cn("text-[11px] font-mono", overLimit ? "text-[color:#FF7A8A]" : "text-muted-foreground")} data-testid="asset-charcount">{charCount}{asset.char_limit ? `/${asset.char_limit}` : ""}</span>
                  <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={cancelEdit} data-testid="asset-edit-cancel">Cancel</Button>
                  <Button size="sm" className="gap-1.5" onClick={() => { onSave(); setMode("view"); }} data-testid="asset-save" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
                    <Save className="h-3.5 w-3.5" /> Save
                  </Button>
                </div>
              )}
            </div>

            {mode === "edit" ? (
              <div className="mt-4 space-y-4">
                <FieldBlock label="Hook" sublabel="The first line — the only thing most people read." count={hookCount} limit={asset.platform === "x" ? 280 : null}>
                  <Textarea data-testid="asset-edit-hook" value={draft.hook} onChange={(e) => setDraft((d) => ({ ...d, hook: e.target.value }))} rows={2} className="resize-none text-[15px] leading-[1.55]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                </FieldBlock>
                <FieldBlock label="Body" sublabel="The full post copy." count={bodyCount}>
                  <Textarea data-testid="asset-edit-body" value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} rows={12} className="resize-y text-[14px] leading-[1.65]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                </FieldBlock>
                <FieldBlock label="Call to action" sublabel="The closing nudge." count={ctaCount}>
                  <Textarea data-testid="asset-edit-cta" value={draft.cta} onChange={(e) => setDraft((d) => ({ ...d, cta: e.target.value }))} rows={2} className="resize-none text-[14px]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                </FieldBlock>
                <FieldBlock label="Hashtags" sublabel="Space-separated, each starts with #.">
                  <Input data-testid="asset-edit-hashtags" value={draft.hashtags} onChange={(e) => setDraft((d) => ({ ...d, hashtags: e.target.value }))} placeholder="#AI #Agents #Operators" className="font-mono text-[13px]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                </FieldBlock>
              </div>
            ) : mode === "revise" ? (
              <div className="mt-4 space-y-2" data-testid="asset-revise-box">
                <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Tell the agent what to change</Label>
                <Textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3} autoFocus placeholder="e.g. Make it punchier, cut the hype, add a stronger CTA." className="resize-none" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} data-testid="asset-revise-input" />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="gap-1.5" onClick={runRevise} disabled={Boolean(busy)} data-testid="asset-revise-send" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
                    {busy === "revise" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Send to agent
                  </Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => { setInstruction(""); setMode("view"); }} data-testid="asset-revise-cancel">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-2.5" data-testid="asset-readonly">
                <p className="whitespace-pre-wrap text-[15px] font-semibold leading-[1.5] text-1">{draft.hook || "—"}</p>
                {draft.body && <p className="whitespace-pre-wrap text-[14px] leading-[1.65] text-foreground/90">{draft.body}</p>}
                {draft.cta && draft.cta !== "—" && <p className="whitespace-pre-wrap text-[13.5px] text-foreground/80">{draft.cta}</p>}
                {draft.hashtags && <p className="font-mono text-[12.5px] text-[color:var(--accent-cyan)]">{draft.hashtags}</p>}
                <p className="pt-1 text-[11px] text-muted-foreground">Last saved {lastSaved ? timeAgo(lastSaved) : "—"} · click Edit to change the wording.</p>
              </div>
            )}
          </div>

          {/* Cover image */}
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="asset-cover-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Cover image</p>
                <p className="mt-1 text-[12.5px] text-muted-foreground">Generate or upload a cover — it appears on the post preview.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {asset.cover_image && (
                  <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => updateAsset(asset.id, { cover_image: null })} data-testid="asset-cover-remove">
                    <XIcon className="h-3.5 w-3.5" /> Remove
                  </Button>
                )}
                <Button size="sm" variant="outline" className="gap-1.5" onClick={onGenerateCover} disabled={coverBusy} data-testid="asset-cover-generate">
                  {coverBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  {asset.cover_image ? "Regenerate" : "Generate cover"}
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => document.getElementById("asset-cover-input")?.click()} disabled={coverBusy} data-testid="asset-cover-upload">
                  <Upload className="h-3.5 w-3.5" /> Upload
                </Button>
                <input id="asset-cover-input" type="file" accept="image/*" className="hidden" onChange={onUploadCover} data-testid="asset-cover-input" />
              </div>
            </div>
            <textarea
              value={coverPrompt}
              onChange={(e) => setCoverPrompt(e.target.value)}
              rows={2}
              placeholder="Describe the image you want…"
              data-testid="asset-cover-prompt"
              className="mt-3 w-full resize-none rounded-md border bg-[color:var(--surface-2)] px-2.5 py-2 text-[12.5px] text-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--accent-cyan)]/50"
              style={{ borderColor: "var(--border-subtle)" }}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Tip: describe a scene, not words to render. The free model is told to avoid text.</p>
            {asset.cover_image && (
              <div className="mt-3 overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
                <img src={asset.cover_image} alt="Cover" className="block max-h-[520px] w-full object-contain" />
              </div>
            )}
          </div>

          {/* Instagram card — per-post Auto / Brand / Manual, before approve/publish */}
          {asset.platform === "instagram" && (
            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="asset-igcard-card">
              <p className="mb-2 text-xs uppercase tracking-[0.16em] text-muted-foreground">Instagram card</p>
              <IgCardControl asset={asset} updateAsset={updateAsset} variant="light" />
            </div>
          )}

          {/* Decide — just approve or reject. Editing lives above. */}
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Decide</p>
            <p className="mt-1 text-[13px] text-foreground/85">
              {workflow.canApprove
                ? "Happy with it? Approve to move it toward scheduling. Otherwise reject it."
                : "This asset has already moved forward. Edit above before publishing, or reject it."}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {workflow.canApprove && (
                <Button size="sm" onClick={onApproveAsset} className="gap-1.5 font-medium" data-testid="asset-approve-button" style={{ backgroundColor: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.24)", color: "#15803D" }}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => { rejectAsset(asset.id); toast.error("Rejected & archived"); }} className="gap-1.5 text-muted-foreground hover:text-[color:var(--accent-red)]" data-testid="asset-reject-button">
                <Trash2 className="h-3.5 w-3.5" /> Reject
              </Button>
            </div>
          </div>

          <RevisionHistoryPanel
            revisions={revisionHistory}
            onRevert={(entry) => { revertAsset(asset.id, entry.id); toast.success("Reverted to the earlier version"); }}
          />
        </div>

        {/* Live preview column (sticky) */}
        <aside className="xl:col-span-5 xl:sticky xl:top-20 xl:self-start" data-testid="asset-live-preview">
          <PlatformPreviewPanel
            asset={livePreviewAsset}
            title="Preview"
            note="This is how the post will look on the channel. Use Edit / Revise / Regenerate on the left to change it."
            mobilePreviewContext="editor"
            footer={(
              <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                Approving locks the final wording for scheduling. You can still edit before publishing.
              </p>
            )}
          />
        </aside>
      </section>
    </motion.div>
  );
}

function AssetWorkflowStrip({ status }) {
  const currentIndexByStatus = {
    draft: 0,
    needs_review: 0,
    approved: 1,
    scheduled: 2,
    publishing: 2,
    published: 3,
  };
  const currentIndex = currentIndexByStatus[status] ?? 0;
  const archived = status === "archived";
  const steps = [
    { label: "Review", icon: ListChecks },
    { label: "Approved", icon: CheckCircle2 },
    { label: "Scheduled", icon: CalendarPlus },
    { label: "Published", icon: ExternalLink },
  ];

  return (
    <section
      className="rounded-xl border bg-card p-3 inner-highlight"
      style={{ borderColor: "var(--border-subtle)" }}
      data-testid="asset-workflow-strip"
    >
      <div className="grid gap-2 md:grid-cols-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const complete = !archived && index < currentIndex;
          const active = !archived && index === currentIndex;
          return (
            <div
              key={step.label}
              className={cn(
                "relative rounded-lg border p-3",
                active ? "bg-[#0C0F0E] text-white" : "bg-[color:var(--surface-1)] text-foreground",
                complete && "bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]",
                archived && "opacity-60",
              )}
              style={{ borderColor: active ? "rgba(18, 22, 20,0.82)" : "var(--border-subtle)" }}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn(
                    "inline-flex h-8 w-8 items-center justify-center rounded-md border",
                    active ? "border-white/15 bg-white/10 text-[color:var(--text-1)]" : "border-[color:var(--border-subtle)] bg-card text-muted-foreground",
                    complete && "border-emerald-200 bg-card text-[color:var(--accent-green)]",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className={cn("text-[10.5px] uppercase tracking-[0.16em]", active ? "text-slate-300" : "text-muted-foreground")}>
                  {complete ? "Done" : active ? "Now" : "Next"}
                </span>
              </div>
              <p className={cn("mt-2 text-sm font-semibold", active ? "text-white" : "text-foreground")}>{step.label}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReviewHandoff({ asset, workflow, tone, onApprove, onCopy, onPublish, publishing }) {
  const toneMap = {
    review: {
      border: "rgba(217,119,6,0.24)",
      bg: "rgba(217,119,6,0.08)",
      iconBg: "rgba(217,119,6,0.12)",
      icon: "#B45309",
      Icon: Wand2,
    },
    approved: {
      border: "rgba(22,163,74,0.24)",
      bg: "rgba(22,163,74,0.08)",
      iconBg: "rgba(22,163,74,0.12)",
      icon: "#15803D",
      Icon: CheckCircle2,
    },
    scheduled: {
      border: "rgba(18, 147, 79,0.22)",
      bg: "rgba(18, 147, 79,0.08)",
      iconBg: "rgba(18, 147, 79,0.12)",
      icon: "#0B7C42",
      Icon: CalendarPlus,
    },
    published: {
      border: "rgba(100,116,139,0.24)",
      bg: "rgba(100,116,139,0.08)",
      iconBg: "rgba(100,116,139,0.12)",
      icon: "#475569",
      Icon: ExternalLink,
    },
  };
  const cfg = toneMap[tone] || toneMap.review;
  const Icon = cfg.Icon;

  return (
    <section
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 inner-highlight"
      style={{ borderColor: cfg.border, backgroundColor: cfg.bg }}
      data-testid="asset-decision-handoff"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: cfg.iconBg, color: cfg.icon }}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-1">{workflow.handoffTitle}</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">{workflow.handoffCopy}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={onCopy} data-testid="asset-handoff-copy">
          <Copy className="h-3.5 w-3.5" /> Copy
        </Button>
        {workflow.canApprove && (
          <Button size="sm" className="gap-1.5" onClick={onApprove} data-testid="asset-handoff-approve" style={{ backgroundColor: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.24)", color: "#15803D" }}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
          </Button>
        )}
        {workflow.canSchedule && (
          <ScheduleAssetDialog
            asset={asset}
            trigger={(
              <Button size="sm" className="gap-1.5" data-testid="asset-handoff-schedule" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
                <CalendarPlus className="h-3.5 w-3.5" /> {workflow.scheduleLabel}
              </Button>
            )}
          />
        )}
        {asset.status === "scheduled" && (
          <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid="asset-handoff-calendar">
            <Link to="/calendar"><CalendarPlus className="h-3.5 w-3.5" /> View Calendar</Link>
          </Button>
        )}
        {(asset.status === "approved" || asset.status === "scheduled") && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={onPublish}
            disabled={publishing}
            data-testid="asset-handoff-publish"
            style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}
          >
            {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Publish now
          </Button>
        )}
      </div>
    </section>
  );
}

function MetaCard({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="rounded-xl border p-3.5 inner-highlight" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
      <p className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">
        <Icon className="h-3 w-3 text-[color:var(--accent-cyan)]" />
        {label}
      </p>
      <div className={cn("mt-1.5 text-[13px] text-1 truncate", mono && "font-mono")}>{value}</div>
    </div>
  );
}

function FieldBlock({ label, sublabel, count, limit, warning, children }) {
  return (
    <div className="rounded-xl border p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</Label>
          {sublabel && <p className="mt-0.5 text-[11.5px] text-muted-foreground/85">{sublabel}</p>}
        </div>
        {count !== undefined && (
          <span className={cn("font-mono text-[11px] text-muted-foreground", warning && "text-[color:#FF7A8A]")}>
            {count}{limit ? `/${limit}` : ""}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function RevisionHistoryPanel({ revisions, onRevert }) {
  const latest = revisions.slice().reverse();

  return (
    <section
      className="rounded-xl border p-4 inner-highlight"
      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
      data-testid="asset-revision-history"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">
            <History className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
            Revision history
          </p>
          <p className="mt-1 text-[13px] text-foreground/85">
            See what the agent changed before this draft moved forward.
          </p>
        </div>
        <span className="rounded-md border px-2 py-1 text-[11px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          {revisions.length} {revisions.length === 1 ? "revision" : "revisions"}
        </span>
      </div>

      {latest.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed p-4 text-[13px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          No revisions yet. When you revise or regenerate this post, the instruction and summary will appear here.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {latest.map((entry) => (
            <article key={entry.id || entry.created_at} className="rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-[13px] font-semibold capitalize text-1">{entry.mode || "Revision"}</p>
                  <p className="mt-1 text-[12px] text-muted-foreground">{entry.summary || "Updated the draft"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-muted-foreground">{entry.created_at ? timeAgo(entry.created_at) : "Just now"}</span>
                  {onRevert && entry.before ? (
                    <button
                      type="button"
                      onClick={() => onRevert(entry)}
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium text-[color:var(--text-2)] transition-colors hover:border-[color:var(--accent-blue)]/50 hover:text-1"
                      style={{ borderColor: "var(--border-subtle)" }}
                      data-testid={`asset-revert-${entry.id}`}
                    >
                      <Undo2 className="h-3 w-3" /> Revert
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="mt-3 rounded-md border p-2" style={{ borderColor: "var(--border-subtle)" }}>
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">Instruction</p>
                <p className="mt-1 text-[12.5px] text-foreground/85">{entry.instruction || "No instruction recorded"}</p>
              </div>
              {entry.after?.body && (
                <p className="mt-3 line-clamp-3 text-[12.5px] leading-relaxed text-muted-foreground">
                  {entry.after.body}
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
